import { IconType } from "@jsq/ds/icons";
import { RiskRating as RiskRatingEnum } from "~/graphql/supergraphEnums";
import { RiskRating } from "~/graphql/supergraphTypes";
import { shouldShowRiskRating } from "../utils";
import { RISK_RATING_PROPS, RISK_VALUES } from "./constants";
import { RiskRatingInfo } from "./types";

/**
 * Gets the appropriate risk rating value considering program type.
 * Shows N/A for non-AML programs (programId: 1 = Screening, 2 = KYC).
 * Shows actual risk rating for AML programs (3 = US_AML, 4 = CAYMANS_AML, 5 = LUXEMBOURG_AML).
 *
 * @param riskRating - The risk rating object containing overriddenRating and calculatedRating
 * @param programId - Optional program ID to determine if risk rating should be shown
 * @returns RiskRating enum value
 */
export const getRiskRating = (
  riskRating?: RiskRatingInfo | null,
  programId?: number,
): RiskRating => {
  // If no program info or non-AML programs, always return None.
  if (programId === undefined || !shouldShowRiskRating(programId)) {
    return RiskRatingEnum.None;
  }

  // For AML programs (Programs.US_AML, Programs.CAYMANS_AML, Programs.LUXEMBOURG_AML), return actual rating
  // Prioritizes overriddenRating over calculatedRating, falls back to None if both are null/undefined
  const rating =
    riskRating?.overriddenRating ?? riskRating?.calculatedRating ?? RiskRatingEnum.None;
  return rating;
};

export const sortRiskRating = (a: RiskRating, b: RiskRating) => {
  return (
    (RISK_RATING_PROPS[a].sortPosition ?? 0) - (RISK_RATING_PROPS[b].sortPosition ?? 0) || 0
  );
};

export const getPositionRiskRating = (
  investorRating?: RiskRating | null,
  beneficiaryRating?: RiskRating | null,
  paymentAccountRating?: RiskRating | null,
): RiskRating => {
  const ratings = [investorRating, beneficiaryRating, paymentAccountRating].filter(
    (rating): rating is RiskRating => rating !== null,
  );

  if (ratings.length === 0) return RiskRatingEnum.None;

  return ratings.reduce((highest, current) =>
    RISK_VALUES[current] > RISK_VALUES[highest] ? current : highest,
  );
};

export const calculateRiskRatingChange = (
  currentRiskRating?: RiskRating | null,
  proposedRiskRating?: RiskRating | null,
): IconType | null => {
  if (!currentRiskRating || !proposedRiskRating) return null;

  const current = RISK_RATING_PROPS[currentRiskRating]?.sortPosition ?? 0;
  const proposed = RISK_RATING_PROPS[proposedRiskRating]?.sortPosition ?? 0;
  if (current > proposed) return "arrow-down";
  if (current < proposed) return "arrow-up";
  return "dash";
};
